import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuResizeObserver = null;
  #deactivateTimer = null;
  #isToggling = false; 

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('keydown', this.#handleKeydown);
    // CLICK-TO-OPEN: wire the click handling up ourselves, since the theme markup
    // does not call toggle on click.
    this.addEventListener('click', this.#handleMenuClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('click', this.#handleMenuClick);
    this.#cleanupResizeObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    // HOVER DISABLED: leaving the overflow menu with the mouse no longer closes it.
  };

  #state = {
    activeItem: null,
  };

  #pointerIdleTimer;
  #lastPointer = { x: 0, y: 0 };

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 150);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        if (listItem !== this.#state.activeItem) {
            listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
        }
      }
    });
  }

  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    // HOVER DISABLED: the menu now only opens via a click (the toggle method).
    // Any pointerenter/hover events that the theme wires up are ignored here.
    if (event?.type !== 'toggleClick') {
      return;
    }

    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      void submenu.offsetWidth; 

      this.#cleanupResizeObserver();

      this.#submenuResizeObserver = new ResizeObserver((entries) => {
        requestAnimationFrame(() => {
          for (let entry of entries) {
            let height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight;
            
            if (!isDefaultSlot) {
               const overflowListHeight = this.#getOverflowListLinksHeight();
               if (hasSubmenu) {
                  const overflowHeight = this.overflowMenu?.offsetHeight || 0;
                  height = Math.max(overflowHeight, overflowListHeight);
               } else {
                  height = overflowListHeight;
               }
            }
            
            if (height > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
              this.#setFullOpenHeaderHeight(height);
            }
          }
        });
      });
      
      this.#submenuResizeObserver.observe(submenu);
    }

    let finalHeight = submenu?.offsetHeight || submenu?.scrollHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) finalHeight = 0;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    // HOVER DISABLED: pointer tracking (the hover "safety box") is no longer started.
  };

  #lastToggleTime = 0;

  #handleMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Clicks inside an open submenu are normal links — leave them alone.
    if (event.target.closest('[ref="submenu[]"]')) return;

    // Find which top-level tab was clicked. Prefer locating the actual menu
    // link and using its container, falling back to the list item class.
    const menuLink = event.target.closest('[ref="menuitem"]');
    const li = menuLink?.parentElement ?? event.target.closest('.menu-list__list-item');
    if (!li) return;

    this.toggle({
      currentTarget: li,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => event.stopPropagation(),
    });
  };

  toggle = (event) => {
    // Safety guard: ignore a duplicate call from the same click,
    // in case the theme also fires toggle through its own wiring.
    const now = Date.now();
    if (now - this.#lastToggleTime < 100) return;

    if (!(event.currentTarget instanceof Element) || !this.headerComponent) return;

    const li = event.currentTarget;
    const item = findMenuItem(li);
    if (!item) return;

    const isDefaultSlot = /** @type {HTMLElement} */ (li).slot === '';
    const hasSubmenu = Boolean(findSubmenu(item));

    if (!hasSubmenu && isDefaultSlot) return;

    // Only record the timestamp for calls that actually get this far —
    // a failed call must not block a working one from the same click.
    this.#lastToggleTime = now;

    event.preventDefault();
    event.stopPropagation(); 

    this.#isToggling = true;
    setTimeout(() => { this.#isToggling = false; }, 400);

    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      document.removeEventListener('click', this.#handleOutsideClick);
      return;
    }

    this.activate({ target: li, type: 'toggleClick' });
    
    setTimeout(() => {
      document.addEventListener('click', this.#handleOutsideClick);
    }, 0);
  };

  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    if (this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem);
    }
    document.removeEventListener('click', this.#handleOutsideClick);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    this.#deactivate(this.#state.activeItem);
    document.removeEventListener('click', this.#handleOutsideClick);
  };

  deactivate(event) {
    // HOVER DISABLED: moving the mouse away no longer closes the menu.
    // The menu now closes only via: re-clicking the tab, clicking outside, or pressing Escape.
    return;
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);

    // 2. Trigger the CSS variables for the reverse animation
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');

    // 3. IMMEDIATELY update the ARIA states so the theme's CSS knows to execute the animation curve
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);
    
    this.#state.activeItem = null;
    this.#cleanupResizeObserver();

    if (this.#deactivateTimer) clearTimeout(this.#deactivateTimer);

    // 4. Defer the structural visibility deletion until the CSS animation finishes
    this.#deactivateTimer = setTimeout(() => {
      if (submenu) {
        delete submenu.dataset.active;
      }
      this.#deactivateTimer = null;
    }, 300);
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) cb(submenu);
      });
    };

    mapSubmenus((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => submenu.style.removeProperty('display'));
    
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}